import IosShareIcon from '@mui/icons-material/IosShare';
import { Tag, Typography } from 'antd';
import FileSaver from 'file-saver';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AccountAvatar from '../../../components/AccountAvatar';
import StatusBadge from '../../../components/Badges/StatusBadge';
import AlumlyButton from '../../../components/AlumlyButton';
import AlumlyDataGrid from '../../../components/AlumlyDataGrid';
import { PermissionEnum } from '../../../enums/permission';
import { RouteNameEnum } from '../../../enums/routeName';
import { ServiceNameEnum } from '../../../enums/serviceName';
import usePermission from '../../../hooks/usePermission';
import useServices from '../../../hooks/useServices';
import { AxiosService } from '../../../utils/axiosService';
import { DateTimeUtils } from '../../../utils/dateTime';
import { NumberFormatUtils } from '../../../utils/numberFormat';
import { PathBuilder } from '../../../utils/pathBuilder';
import FormFilterCustomerList, { FilterCustomerListT } from './FormFilterCustomerList';

export type WalletT = {
  id: number;
  productType: string;
  balance: number;
  expiredAt: Date;
};

const UserList: React.FC = () => {
  const navigate = useNavigate();
  const { checkPermission } = usePermission();
  const [customerList, setCustomerList] = useState<any>([]);
  const [selectedFilter, setSelectedFilter] = useState<FilterCustomerListT>();
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 10,
    rowCount: 0,
    loading: false,
  });

  const { service: getCustomerListService } = useServices(ServiceNameEnum.customerMgmt.index);

  const loadCustomerList = async (): Promise<void> => {
    const pageOptions = { page: pagination.page + 1 };
    const queryString = {
      ...pageOptions,
      searchQuery: selectedFilter?.searchQuery,
      registeredType: selectedFilter?.registeredType,
      // gender: selectedFilter?.gender,
      createdAtFromDate: selectedFilter?.createdAtFromDate ? new Date(selectedFilter?.createdAtFromDate) : null,
      createdAtToDate: selectedFilter?.createdAtToDate ? new Date(selectedFilter?.createdAtToDate) : null,
      status: selectedFilter?.status,
      memberStatus: selectedFilter?.memberStatus,
      alumniStatus: selectedFilter?.alumniStatus,
    };
    const { data, error } = await getCustomerListService.get({ queryString });
    if (data) {
      setCustomerList(data.items);
      setPagination({
        page: data.pagination.currentPage - 1,
        pageSize: data.pagination.perPage,
        rowCount: data.pagination.total,
        loading: false,
      });
    } else if (error) {
      const errorResponse = AxiosService.getErrorResponse(error);
      toast.error(errorResponse?.message, { toastId: 'allToast' });
    }
  };

  const { service: exportCustomerService } = useServices(ServiceNameEnum.customerMgmt.exportCustomer);
  const handleClickExport = async (): Promise<void> => {
    const filterOptions = selectedFilter;
    const queryString = { ...filterOptions };
    const { data, error } = await exportCustomerService.get({ queryString });
    if (data) {
      const blob = new Blob([data]);
      FileSaver.saveAs(blob, 'customer.csv');
    } else if (error) {
      const errorResponse = AxiosService.getErrorResponse(error);
      toast.error(errorResponse?.message);
    }
  };
  const hasDetailAccessPermission = checkPermission([
    PermissionEnum.code.customerViewDetail,
    PermissionEnum.code.customerViewNonApp,
    PermissionEnum.code.customerViewVoucherList,
    PermissionEnum.code.customerViewPoint,
    PermissionEnum.code.customerViewQuestionnaire,
    PermissionEnum.code.customerViewCreditSummary,
    PermissionEnum.code.customerViewTransaction,
    PermissionEnum.code.customerViewPrimary,
    PermissionEnum.code.customerViewNominee,
    PermissionEnum.code.customerViewActivityLog,
  ]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, loading: true }));
    loadCustomerList();
  }, [pagination.page, selectedFilter]);

  const columns: any[] = [
    {
      field: 'image',
      headerName: '',
      width: 70,
      sortable: false,
      renderCell: (params: any) => (
        <AccountAvatar backgroundImageURL={params.row.imageURL} minWidth="44px" maxHeight="44px" />
      ),
    },
    {
      field: 'customerAccount',
      headerName: 'Customer account',
      width: 250,
      // sort by email
      valueGetter: (params: any) => params.row.email,
      renderCell: (params: any) => (
        <div>
          <div style={{ fontSize: 14, wordWrap: 'break-word', whiteSpace: 'normal' }}>
            {params.row.email}
          </div>
          <div style={{ color: 'gray' }}>
            {`${params.row.displayName}`}
          </div>
        </div>
      ),
    },
    {
      field: 'accountType',
      headerName: 'Account type',
      width: 150,
      sortable: false,
      valueGetter: (params: any) =>
        `${params.row.accessMethods[0].description}`,
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 120,
      sortable: false,
      renderCell: (params: any) => (
        <div>
          <StatusBadge status={params.row.isActive} />
        </div>
      ),
    },
    {
      field: 'alumniStatus',
      headerName: 'Alumni',
      width: 120,
      sortable: false,
      renderCell: (params: any) => {
        const status = params.row?.alumniStatus || 'pending';
        const colorMap: Record<string, string> = { pending: 'orange', approved: 'green', rejected: 'red' };
        const labelMap: Record<string, string> = { pending: 'Pending', approved: 'Approved', rejected: 'Rejected' };
        return (
          <Tag
            color={colorMap[status] || 'orange'}
            style={{
              width: '85px',
              textAlign: 'center',
              fontSize: '10px',
              fontWeight: 'bold',
              lineHeight: '22px',
              height: '24px',
              margin: 0,
            }}
          >
            {labelMap[status] || 'Pending'}
          </Tag>
        );
      },
    },
    {
      field: 'userType',
      headerName: 'User type',
      width: 120,
      sortable: false,
      valueGetter: (params: any) => {
        const labels: Record<string, string> = {
          alumni: 'Alumni',
          bba_student: 'BBA Student',
          faculty: 'Faculty',
          employer: 'Employer',
        };
        return labels[params.row?.userType] || 'Alumni';
      },
    },
    // {
    //   field: 'faculty',
    //   headerName: 'Faculty',
    //   width: 150,
    //   sortable: false,
    //   valueGetter: (params: any) => params.row?.faculty || '-',
    // },
    {
      field: 'graduationYear',
      headerName: 'SHI',
      width: 100,
      sortable: false,
      valueGetter: (params: any) => params.row?.graduationYear || '-',
    },
    {
      field: 'batch',
      headerName: 'รุ่นที่ BBA',
      width: 80,
      sortable: false,
      valueGetter: (params: any) => params.row?.batch || '-',
    },
    {
      field: 'createdAt',
      headerName: 'Created date',
      width: 180,
      flex: 1,
      cellClassName: 'text-wrap',
      valueGetter: (params: any) =>
        `${DateTimeUtils.dateTimeFormat(new Date(params.row.createdAt))}`,
    },
    {
      field: 'lastUpdate',
      headerName: 'Last updated',
      width: 180,
      flex: 1,
      cellClassName: 'text-wrap',
      valueGetter: (params: any) =>
        `${DateTimeUtils.dateTimeFormat(new Date(params.row.updatedAt))}`,
    },
    // {
    //   field: 'creditBalance',
    //   headerName: 'Credit balance',
    //   width: 100,
    //   flex: 1,
    //   cellClassName: 'text-wrap',
    //   valueGetter: (params: any) =>
    //     `฿ ${NumberFormatUtils.numberToPrecisionFormat(params.row?.customerMember?.creditBalance || 0)}`,
    // },
  ];

  const onRowClick = (rowData: any): void => {
    const params = { id: rowData.id };
    const detailPath = PathBuilder.buildPath(RouteNameEnum.customer.detail, { params });
    navigate(detailPath);
  };

  const onPageChange = (page: number): void => {
    setPagination((prev) => ({
      ...prev,
      page,
    }));
  };

  const resetPage = (): void => {
    setPagination({
      ...pagination,
      page: 0,
    });
  };

  return (
    <div style={{ marginTop: 32 }}>
      <FormFilterCustomerList
        resetPage={resetPage}
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
      />
      <div style={{
        marginTop: 32,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
      }}
      >
        <div style={{ display: 'flex' }}>
          <Typography.Text>Total</Typography.Text>
          &nbsp;
          <Typography.Text style={{ fontWeight: 700 }}>{`${pagination.rowCount} items`}</Typography.Text>
        </div>
        { checkPermission([PermissionEnum.code.customerExportList])
          && (
          <div style={{
            display: 'flex', flexDirection: 'row', alignItems: 'center',
          }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <AlumlyButton
                width={135}
                icon={<IosShareIcon />}
                label="rxv"
                onClick={handleClickExport}
              >
                <span>Export CSV</span>
              </AlumlyButton>
            </div>
          </div>
          )}
      </div>
      <div style={{ marginTop: 8 }}>
        <AlumlyDataGrid
          rows={customerList}
          onRowClick={hasDetailAccessPermission ? onRowClick : undefined}
          columns={columns}
          {...pagination}
          paginationMode="server"
          onPageChange={onPageChange}
          height={708}
        />
      </div>
    </div>
  );
};

export default UserList;
